import { componentText, HostAriaLabelMixin, SchmancyElement } from '@schmancy/mixins/index'
import { color } from '@schmancy/directives'
import { css, html } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { SchmancyTheme } from '@schmancy/theme'
import { BehaviorSubject, fromEvent, merge, timer } from 'rxjs'
import { takeUntil, tap, filter } from 'rxjs/operators'

/**
 * A single Material Design 3 navigation-bar destination — icon and optional label.
 *
 * @element schmancy-navigation-bar-item
 * @slot icon - Slot for custom icon content
 * @slot - Default slot for custom content
 *
 * @fires bar-item-click - When the item is clicked
 * @fires focus - When the item receives focus
 * @fires blur - When the item loses focus
 *
 * @example
 * <schmancy-navigation-bar-item icon="home" label="Home" active></schmancy-navigation-bar-item>
 *
 * @example
 * <schmancy-navigation-bar-item label="Custom">
 *   <span slot="icon">🏠</span>
 * </schmancy-navigation-bar-item>
 */
@customElement('schmancy-navigation-bar-item')
export class SchmancyNavigationBarItem extends HostAriaLabelMixin(SchmancyElement) {
	static styles = [
		css`
			:host {
				display: flex;
				flex: 1;
				min-width: 48px;
				max-width: 168px;
				user-select: none;
				-webkit-tap-highlight-color: transparent;
			}

			:host([disabled]) {
				pointer-events: none;
			}

			button {
				font-family: inherit;
				border: none;
				background: none;
				width: 100%;
				padding: 0;
				margin: 0;
				text-align: center;
				color: inherit;
			}

			button:focus {
				outline: none;
			}

			button:focus-visible {
				outline: 2px solid var(--focus-color);
				outline-offset: 2px;
				border-radius: var(--schmancy-radius-lg, 0.625rem); /* mirrors --radius-lg (rounded-lg) */
			}

			@keyframes ripple {
				to {
					transform: scale(4);
					opacity: 0;
				}
			}

			.ripple-effect {
				position: absolute;
				border-radius: var(--schmancy-radius-circle, 50%);
				width: 20px;
				height: 20px;
				background-color: currentColor;
				opacity: 0.25;
				animation: ripple 0.6s ease-out;
				pointer-events: none;
			}
		`,
	]
	@property({ type: String })
	icon = ''

	/**
	 * Names this destination, visibly and to the accessibility tree alike. Unset,
	 * the name falls to `icon` — which still says which destination this is,
	 * where a shared fallback would leave every icon-only item in the bar
	 * answering to the same one — and a Material Symbols token reads as a token,
	 * so an icon-only bar still wants this set.
	 */
	@property({ type: String })
	label = ''

	@property({ type: String })
	badge = ''

	private active$ = new BehaviorSubject<boolean>(false)

	@property({ type: Boolean, reflect: true })
	get active() {
		return this.active$.value
	}
	set active(value: boolean) {
		this.active$.next(value)
	}

	@property({ type: Boolean, reflect: true })
	disabled = false

	@property({ type: Boolean, reflect: true })
	hideLabels = false

	@state()
	private ripples: { x: number; y: number; id: number }[] = []

	private addRippleEffect = (event: MouseEvent) => {
		if (this.disabled) return

		const indicatorDiv = this.shadowRoot?.querySelector('.w-16.h-8')
		if (indicatorDiv) {
			const rect = indicatorDiv.getBoundingClientRect()
			const ripple = {
				x: event.clientX - rect.left,
				y: event.clientY - rect.top,
				id: Date.now(),
			}
			this.ripples = [...this.ripples, ripple]

			timer(600)
				.pipe(
					tap(() => {
						this.ripples = this.ripples.filter(r => r.id !== ripple.id)
					}),
					takeUntil(this.disconnecting),
				)
				.subscribe()
		}
	}

	private handleClick = (event: MouseEvent) => {
		if (this.disabled) {
			event.preventDefault()
			event.stopPropagation()
			return
		}

		this.addRippleEffect(event)

		// bar-item-click fires from the click$/keyActivation$ stream in
		// setupNavigationStream, not here.
	}

	private handleKeyDown = (event: KeyboardEvent) => {
		if (this.disabled) return

		// Enter/Space activation is handled by the click$/keyActivation$ stream
		// in setupNavigationStream, not here.
		if (event.key === 'Enter' || event.key === ' ') {
			event.preventDefault()
		}
	}

	setActive(isActive: boolean) {
		this.active = isActive
	}

	connectedCallback() {
		super.connectedCallback()

		this.active$.pipe(takeUntil(this.disconnecting)).subscribe(() => {
			this.requestUpdate()
		})

		this.setupNavigationStream()
	}

	private setupNavigationStream() {
		const button = this.shadowRoot?.querySelector('button')
		if (!button) return

		const click$ = fromEvent<MouseEvent>(button, 'click').pipe(filter(() => !this.disabled))

		const keyActivation$ = fromEvent<KeyboardEvent>(button, 'keydown').pipe(
			filter(() => !this.disabled),
			filter(event => event.key === 'Enter' || event.key === ' '),
			tap(event => event.preventDefault()),
		)

		merge(click$, keyActivation$)
			.pipe(
				tap(() => {
					this.dispatchEvent(
						new CustomEvent('bar-item-click', {
							detail: {
								icon: this.icon,
								label: this.label,
								active: this.active,
							},
							bubbles: true,
							composed: true,
						}),
					)
				}),
				takeUntil(this.disconnecting),
			)
			.subscribe()
	}

	private formatBadge(badge: string): string {
		const num = Number(badge)
		if (!isNaN(num)) {
			return num > 99 ? '99+' : String(num)
		}
		return badge.slice(0, 3)
	}

	protected firstUpdated() {
		// The connectedCallback call was a same-tick no-op (no <button> in the
		// shadow root yet); retry now that the first render has produced one.
		this.setupNavigationStream()
	}

	protected render() {
		const hasSlotContent = this.querySelector('[slot]') || (this.textContent?.trim() && !this.label)
		const badgeText = this.badge ? this.formatBadge(this.badge) : ''
		const showBadgeText = badgeText && badgeText !== '0'

		const hasCustomIcon = this.querySelector('[slot="icon"]')

		const containerClasses = {
			'relative flex flex-col items-center justify-center': true,
			'flex-1 min-w-[48px] max-w-[168px]': true,
			'py-2 px-1 cursor-pointer': !this.disabled,
			'transition-all duration-200': true,
			'hover:bg-surface-containerHigh': !this.disabled && !this.active,
			'cursor-not-allowed opacity-38': this.disabled,
			'outline-none': true,
			'focus-visible:outline-2 focus-visible:outline-offset-2': true,
		}

		// Visual only — the outer <button> is the click target
		const indicatorClasses = {
			'w-16 h-8 rounded-2xl': true,
			'flex items-center justify-center': true,
			'transition-all duration-200': true,
			'bg-secondary-container': this.active,
			'group-hover:bg-surface-containerHighest': !this.active && !this.disabled,
			'relative overflow-hidden': true,
		}

		const labelClasses = {
			'text-xs font-medium leading-4 mt-1': true,
			'text-center max-w-full': true,
			'overflow-hidden text-ellipsis whitespace-nowrap': true,
			'transition-all duration-200': true,
		}

		const containerColors = this.active
			? {
					color: SchmancyTheme.sys.color.secondary.onContainer,
				}
			: {
					color: SchmancyTheme.sys.color.surface.onVariant,
				}

		const styleVars = {
			'--focus-color': SchmancyTheme.sys.color.primary.default,
		}

		return html`
			<button
				type="button"
				class=${this.classMap(containerClasses)}
				@click=${this.handleClick}
				@keydown=${this.handleKeyDown}
				?disabled=${this.disabled}
				aria-pressed=${this.active}
				aria-label=${this.ariaLabel || this.label || this.icon || componentText('navigationBar.item')}
				style=${this.styleMap({
					...styleVars,
					'outline-color': 'var(--focus-color)',
				})}
				${color(containerColors)}
			>
				<div class=${this.classMap(indicatorClasses)}>
					${this.ripples.map(
						ripple => html`
							<span
								class="ripple-effect"
								style=${this.styleMap({
									left: `${ripple.x}px`,
									top: `${ripple.y}px`,
									transform: 'translate(-50%, -50%)',
								})}
							></span>
						`,
					)}
					${
						hasCustomIcon
							? html`<slot name="icon"></slot>`
							: this.icon
								? html`
										<schmancy-icon
											class="relative z-10 flex items-center justify-center transition-all duration-200"
											aria-hidden="true"
										>
											${this.active ? `${this.icon}-fill` : this.icon}
										</schmancy-icon>
									`
								: hasSlotContent
									? html`<slot></slot>`
									: ''
					}
				</div>

				${!this.hideLabels && this.label ? html` <span class=${this.classMap(labelClasses)}>${this.label}</span> ` : ''}
				${showBadgeText ? html`<span class="text-xs text-surface-on-variant" aria-label="${badgeText} notifications">${badgeText}</span>` : this.badge ? html`<span class="text-xs text-surface-on-variant">${componentText('navigationBar.hasNotifications')}</span>` : ''}
			</button>
		`
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'schmancy-navigation-bar-item': SchmancyNavigationBarItem
	}
}
